import { Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { EnumSiNoTodos, ListSiNoTodos, Tc } from 'src/app/components/core/_infrastructure/types/common';
import { FilterSearchingTWRecintoViewModel } from '../recinto.types';
import { TwSolicitudCamViewModelBase } from '../../types/solicitudcam-datosbasicos.types';

@Component({
    selector: 'app-recinto-filter',
    templateUrl: './recinto-filter.component.html',
    styleUrls: ['./recinto-filter.component.scss'],
})
export class RecintoFilterComponent extends ComponentBase implements OnInit, OnChanges {
    @Input() dialogFilter: TwSolicitudCamViewModelBase;
    @Input() provincias: Tc[];
    @Input() municipios: Tc[];
    @Input() tiposRecinto: Tc[];
    @Input() lastFilter: FilterSearchingTWRecintoViewModel;
    @Output() search = new EventEmitter<FilterSearchingTWRecintoViewModel>();
    @Output() cleanForm = new EventEmitter<any>(); // Informa que el formulario se ha reseteado
    @Output() provinciaSelected = new EventEmitter<number>();

    isCollapsed = false;
    listSiNoTodos = ListSiNoTodos;

    constructor(
        private fb: FormBuilder,
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.createForm();
        this.recoverLastSearch();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.form) {
            return;
        }
        if (changes?.lastFilter?.currentValue) {
            this.patchForm();
        }
    }

    private createForm(): void {
        this.form = this.fb.group({
            Titular: '',
            Nif: '',
            IdTipoRecinto: null,
            IdProvincia: null,
            IdMunicipio: null,
            Agregado: null,
            Zona: null,
            Poligono: null,
            Parcela: null,
            Recinto: null,
            EsCompleta: EnumSiNoTodos.TODOS,
            Autorizado: EnumSiNoTodos.TODOS,
        });
        if (this.dialogFilter) {
            this.disableFieldsInDialogMode();
            // Si existe un filtro, es que se está usando éste componente dentro de la ventana de diálogo de selección de recintos para la solicitud de campaña.
            // Indicamos los valores prefijados.
            this.setTipoRecinto(this.dialogFilter.IdTipoSolicitudCam);
        }
    }

    private disableFieldsInDialogMode(): void {
        this.form.patchValue({
            Autorizado: EnumSiNoTodos.SI,
        });
        this.form.controls['Autorizado'].disable();
    }

    private recoverLastSearch(): void {
        if (this.lastFilter) {
            this.patchForm();

            setTimeout(() => {
                this.onSearch(null);
            }, 200);
        }
    }

    /**
     * Recover last search from the previous payload
     */
    private patchForm(): void {
        if (this.lastFilter) {
            this.form.patchValue({
                Titular: this.lastFilter.Titular,
                Nif: this.lastFilter.Nif,
                IdTipoRecinto: this.lastFilter.IdTipoRecinto,
                IdProvincia: this.lastFilter.IdProvincia,
                IdMunicipio: this.lastFilter.IdMunicipio,
                Agregado: this.lastFilter.Agregado,
                Zona: this.lastFilter.Zona,
                Poligono: this.lastFilter.Poligono,
                Parcela: this.lastFilter.Parcela,
                Recinto: this.lastFilter.Recinto,
                EsCompleta: this.lastFilter.EsCompleta,
                Autorizado: this.lastFilter.Autorizado != null ? this.lastFilter.Autorizado : EnumSiNoTodos.TODOS,
            });
            this.form.markAsDirty();
        }
    }

    /**
     * Manejador del evento para limpiar el formulario.
     * @param $event Evento javascript
     */
    onResetForm($event: any): void {
        $event.stopPropagation();
        this.form.setValue({
            Titular: '',
            Nif: '',
            IdTipoRecinto: null,
            IdProvincia: null,
            IdMunicipio: null,
            Agregado: null,
            Zona: null,
            Poligono: null,
            Parcela: null,
            Recinto: null,
            EsCompleta: EnumSiNoTodos.TODOS,
            Autorizado: EnumSiNoTodos.TODOS,
        });
        if (this.dialogFilter) {
            this.disableFieldsInDialogMode();
            this.setTipoRecinto(this.dialogFilter.IdTipoSolicitudCam);
        }
        this.form.markAsPristine();

        this.searchingButton = false;
        this.isCollapsed = false;

        this.cleanForm.emit();
    }

    private setTipoRecinto(id: number): void {
        this.form.patchValue({
            IdTipoRecinto: id,
        });
        this.form.controls['IdTipoRecinto'].disable();
    }

    /**
     * Manejador del evento de búsqueda
     * @param $event Evento Javascript
     */
    onSearch($event: any): void {
        $event?.stopPropagation();
        this.searchingButton = true;
        this.isCollapsed = true;

        const formRawValue = this.form.getRawValue();

        const payload: FilterSearchingTWRecintoViewModel = {
            ...formRawValue,
            PageNumber: this.lastFilter?.PageNumber || 0,
            Asc: this.lastFilter?.Asc || 0,
            OrderBy: this.lastFilter?.OrderBy || '',
        };

        this.search.emit(payload);
    }

    /**
     * Manejador del evento de selección de una Provincia en el desplegable.
     * Ignoramos el valor que viene en el parámetro y lo tomamos del form.
     * @param $event Id y descripción de la provincia seleccionado
     */
    onSelectProvincia($event: any): void {
        const idProvincia = this.form.controls.IdProvincia.value;
        this.provinciaSelected.emit(idProvincia);
        this.setIdMunicipio(null);
    }

    private setIdProvincia(idProvincia: number | null): void {
        this.form.patchValue({
            IdProvincia: idProvincia,
        });
    }

    private setIdMunicipio(idMunicipio: number | null): void {
        this.form.patchValue({
            IdMunicipio: idMunicipio,
        });
    }

    /**
     * Para controlar la propiedad disabled del botón de reset.
     * @returns boolean
     */
    formIsUntouched(): boolean {
        return this.form.pristine || !this.form.dirty;
    }

    /**
     * Para controlar la propiedad disabled del botón de búsqueda.
     * Mientras no estén cargados los grupos, no se habilita el botón.
     * @returns boolean
     */
    get disableSearchBtn(): boolean {
        return this.isCollapsed || !this.provincias || this.provincias?.length == 0;
    }
}
